/**
 * BillingStatus component for Cipher
 *
 * Displays current subscription status with management options.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CreditCard,
  Calendar,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import { Button, Card, Badge, Spinner } from '../ui'
import {
  billingApi,
  redirectToPortal,
  TIER_NAMES,
  TIER_PRICES,
  SubscriptionTier
} from '../../api/billing'
import './BillingStatus.css'

interface BillingStatusProps {
  onManageBilling?: () => void
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  active: {
    color: 'success',
    icon: <CheckCircle size={16} />,
    label: 'Active'
  },
  trialing: {
    color: 'info',
    icon: <Clock size={16} />,
    label: 'Trial'
  },
  past_due: {
    color: 'warning',
    icon: <AlertCircle size={16} />,
    label: 'Past Due'
  },
  canceled: {
    color: 'error',
    icon: <XCircle size={16} />,
    label: 'Canceled'
  },
  unpaid: {
    color: 'error',
    icon: <AlertCircle size={16} />,
    label: 'Unpaid'
  }
}

export function BillingStatus({ onManageBilling }: BillingStatusProps) {
  const [isRedirecting, setIsRedirecting] = useState(false)

  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: billingApi.getStatus,
    refetchOnWindowFocus: true
  })

  const handleManageBilling = async () => {
    if (!status?.stripe_customer_id) {
      return
    }

    setIsRedirecting(true)
    try {
      await redirectToPortal()
      onManageBilling?.()
    } catch (error) {
      console.error('Failed to open billing portal:', error)
      setIsRedirecting(false)
    }
  }

  if (isLoading) {
    return (
      <Card className="billing-status loading">
        <Spinner size="md" />
        <span>Loading billing status...</span>
      </Card>
    )
  }

  if (!status) {
    return (
      <Card className="billing-status error">
        <AlertCircle />
        <span>Unable to load billing status</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw size={14} />
          Retry
        </Button>
      </Card>
    )
  }

  const tier = status.tier as SubscriptionTier
  const tierName = TIER_NAMES[tier]
  const price = TIER_PRICES[tier]
  const statusConfig = STATUS_CONFIG[status.status] || STATUS_CONFIG.active
  const periodEnd = status.current_period_end
    ? new Date(status.current_period_end)
    : null

  return (
    <Card className="billing-status">
      <div className="billing-status-header">
        <div className="tier-info">
          <h3 className="tier-name">{tierName}</h3>
          <Badge variant={statusConfig.color as any}>
            {statusConfig.icon}
            {statusConfig.label}
          </Badge>
        </div>

        {price > 0 && (
          <div className="tier-price">
            <span className="amount">${price}</span>
            <span className="period">/month</span>
          </div>
        )}
      </div>

      {status.cancel_at_period_end && periodEnd && (
        <div className="cancel-warning">
          <AlertCircle size={16} />
          <span>
            Your subscription will end on{' '}
            <strong>{periodEnd.toLocaleDateString()}</strong>
          </span>
        </div>
      )}

      {status.status === 'past_due' && (
        <div className="past-due-warning">
          <AlertCircle size={16} />
          <span>
            Your payment failed. Please update your payment method to continue service.
          </span>
        </div>
      )}

      {periodEnd && !status.cancel_at_period_end && status.status === 'active' && (
        <div className="billing-detail">
          <Calendar size={16} />
          <span>
            Next billing date: <strong>{periodEnd.toLocaleDateString()}</strong>
          </span>
        </div>
      )}

      {status.stripe_customer_id && (
        <div className="billing-actions">
          <Button
            variant="secondary"
            onClick={handleManageBilling}
            loading={isRedirecting}
          >
            <CreditCard size={16} />
            Manage Billing
            <ExternalLink size={14} />
          </Button>
        </div>
      )}

      {tier === 'free' && (
        <p className="free-tier-note">
          Upgrade to unlock more features and higher limits.
        </p>
      )}
    </Card>
  )
}

/**
 * PastDueBanner - shown at top of app when payment has failed
 */
interface PastDueBannerProps {
  onUpdatePayment?: () => void
}

export function PastDueBanner({ onUpdatePayment }: PastDueBannerProps) {
  const [isRedirecting, setIsRedirecting] = useState(false)

  const { data: status } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: billingApi.getStatus
  })

  if (!status || status.status !== 'past_due') {
    return null
  }

  const handleUpdatePayment = async () => {
    setIsRedirecting(true)
    try {
      await redirectToPortal()
      onUpdatePayment?.()
    } catch (error) {
      console.error('Failed to open billing portal:', error)
      setIsRedirecting(false)
    }
  }

  return (
    <div className="past-due-banner">
      <div className="past-due-content">
        <AlertCircle size={18} />
        <span>
          <strong>Payment failed.</strong> Update your payment method to keep access.
        </span>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={handleUpdatePayment}
        loading={isRedirecting}
      >
        Update Payment
      </Button>
    </div>
  )
}

/**
 * CancelAtPeriodEndBanner - shown when subscription is scheduled to cancel
 */
export function CancelAtPeriodEndBanner() {
  const [isReactivating, setIsReactivating] = useState(false)

  const { data: status, refetch } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: billingApi.getStatus
  })

  if (!status || !status.cancel_at_period_end) {
    return null
  }

  const periodEnd = status.current_period_end
    ? new Date(status.current_period_end)
    : null

  const handleReactivate = async () => {
    setIsReactivating(true)
    try {
      await billingApi.reactivateSubscription()
      await refetch()
    } catch (error) {
      console.error('Failed to reactivate:', error)
    } finally {
      setIsReactivating(false)
    }
  }

  return (
    <div className="cancel-banner">
      <div className="cancel-content">
        <Clock size={18} />
        <span>
          Your subscription will end on{' '}
          <strong>{periodEnd?.toLocaleDateString()}</strong>
        </span>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={handleReactivate}
        loading={isReactivating}
      >
        Keep My Subscription
      </Button>
    </div>
  )
}

/**
 * TrialBanner - shown at top of app during trial period
 */
export function TrialBanner() {
  const { data: status } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: billingApi.getStatus
  })

  if (!status || status.status !== 'trialing') {
    return null
  }

  const periodEnd = status.current_period_end
    ? new Date(status.current_period_end)
    : null

  const daysLeft = periodEnd
    ? Math.max(0, Math.ceil((periodEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0

  return (
    <div className="trial-banner">
      <div className="trial-content">
        <Clock size={18} />
        <span>
          <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> left in your free trial
        </span>
      </div>
      <span className="trial-cta">
        Add payment method to continue after trial
      </span>
    </div>
  )
}

export default BillingStatus
